'use client';

import {
  Carousel,
  CarouselProps,
  CarouselRef,
  cn,
  Section,
} from '@scatterlab/zeta-core';
import { memo, RefAttributes, useImperativeHandle, useRef } from 'react';

export type HorizontalScrollRef = CarouselRef;

export type HorizontalScrollProps<Item> = {
  cacheKey?: string;
  containerClassName?: string;
  freeMode?: boolean;
  bounces?: boolean;
} & Omit<CarouselProps<Item>, 'width' | 'height' | 'ref'> &
  RefAttributes<HorizontalScrollRef>;

const HorizontalScroll = <Item,>({
  containerClassName,
  autoHeight,
  slidesPerView = 'auto',
  data,
  renderItem,
  defaultIndex,
  onSnapToItem,
  slidesOffsetBefore,
  freeMode,
  bounces,
  ref,
  ...props
}: HorizontalScrollProps<Item>) => {
  const innerRef = useRef<CarouselRef>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    prev: () => innerRef.current?.prev(),
    next: () => innerRef.current?.next(),
    scrollTo: (index: number, dontAnimate?: boolean) => {
      innerRef.current?.scrollTo(index, dontAnimate);
      if (dontAnimate) {
        onSnapToItem?.(index);
      }
    },
    getCurrentIndex: () => innerRef.current?.getCurrentIndex() ?? 0,
  }));

  if (autoHeight && slidesPerView !== 1) {
    throw new Error('autoHeight can only be used with slidesPerView 1');
  }

  return (
    <Section className={cn('relative flex flex-row', containerClassName)}>
      <Carousel
        {...props}
        onSnapToItem={onSnapToItem}
        ref={innerRef}
        autoHeight={autoHeight}
        slidesPerView={slidesPerView}
        data={data}
        renderItem={renderItem}
        defaultIndex={defaultIndex}
        elementRef={elementRef}
        slidesOffsetBefore={slidesOffsetBefore}
        freeMode={freeMode}
        width={0} // not used in web
      />
    </Section>
  );
};

export default memo(HorizontalScroll) as typeof HorizontalScroll;
